<script setup>
// Audio.vue (chips/Audio.vue)
import { computed, onUnmounted, useTemplateRef, ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { PhoneIncoming, PhoneOutgoing } from 'lucide-vue-next';
import { useMessageContext } from 'dashboard/components-next/message/provider.js';
import { useAccount } from 'dashboard/composables/useAccount';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

// Pega messageType do contexto
const { messageType } = useMessageContext();
const { currentAccount } = useAccount();

// Tipos de mensagem de chamada
const MESSAGE_TYPE_OUTGOING = 1;
const MESSAGE_TYPE_INCOMING_CALL = 4;
const MESSAGE_TYPE_OUTGOING_CALL = 5;

const isCallMessage = computed(() => {
  return [MESSAGE_TYPE_INCOMING_CALL, MESSAGE_TYPE_OUTGOING_CALL].includes(
    messageType?.value
  );
});

const isIncomingCall = computed(
  () => messageType?.value === MESSAGE_TYPE_INCOMING_CALL
);
const isOutgoingCall = computed(
  () => messageType?.value === MESSAGE_TYPE_OUTGOING_CALL
);

// Exibe a transcrição abaixo do áudio conforme a config do Studio
// (transcription_config): recebido respeita show_incoming (default ON), enviado
// só com transcribe_outgoing. Chamadas (4/5) preservam o comportamento atual.
const showTranscription = computed(() => {
  if (!attachment.transcribedText) return false;
  if (isCallMessage.value) return true;
  const config = currentAccount.value?.settings?.transcription_config || {};
  if (messageType?.value === MESSAGE_TYPE_OUTGOING) {
    return config.transcribe_outgoing === true;
  }
  return config.show_incoming !== false;
});

const callStyles = computed(() => {
  if (isIncomingCall.value) {
    return {
      borderColor: 'border-n-teal-6',
      bgColor: 'bg-n-teal-2',
      iconBg: 'bg-n-teal-3',
      textColor: 'text-n-teal-11',
      label: 'Chamada Recebida',
    };
  }
  if (isOutgoingCall.value) {
    return {
      borderColor: 'border-n-iris-6',
      bgColor: 'bg-n-iris-2',
      iconBg: 'bg-n-iris-3',
      textColor: 'text-n-iris-11',
      label: 'Chamada Realizada',
    };
  }
  return null;
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);
const isLoading = ref(true);
const hasError = ref(false);

// Cache global de áudios já carregados
const loadedAudioCache = new Set();

const displayUrl = ref(null);
const retryCount = ref(0);
const maxRetries = 5;
let retryTimeoutId = null;

// Gera chave de cache baseada no attachment ID
const getCacheKey = url => {
  const attachmentId = attachment?.id;
  if (attachmentId) return `audio-${attachmentId}`;
  if (url) return `audio-url-${url.split('/').pop()?.substring(0, 30)}`;
  return null;
};

// Função para testar se o áudio pode ser carregado
const testAudioLoad = url => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.preload = 'metadata';

    const cleanup = () => {
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.src = '';
    };

    audio.onloadedmetadata = () => {
      cleanup();
      resolve(url);
    };

    audio.onerror = () => {
      cleanup();
      reject(new Error('Audio load failed'));
    };

    audio.src = url;
  });
};

const loadAudio = async url => {
  if (!url) return;

  const cacheKey = getCacheKey(url);

  // Se já carregou com sucesso antes, usar direto
  if (cacheKey && loadedAudioCache.has(cacheKey)) {
    displayUrl.value = url;
    isLoading.value = false;
    hasError.value = false;
    return;
  }

  isLoading.value = true;
  hasError.value = false;

  try {
    await testAudioLoad(url);

    // Cache successful load
    if (cacheKey) {
      loadedAudioCache.add(cacheKey);
    }

    displayUrl.value = url;
    isLoading.value = false;
    hasError.value = false;
    retryCount.value = 0;
  } catch {
    if (retryCount.value < maxRetries) {
      retryCount.value += 1;
      const delay = 500 * retryCount.value;

      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }

      retryTimeoutId = setTimeout(() => {
        const currentUrl = timeStampURL.value;
        if (currentUrl) {
          loadAudio(currentUrl);
        }
      }, delay);
    } else {
      isLoading.value = false;
      hasError.value = true;
    }
  }
};

// Watch for URL changes
watch(
  () => timeStampURL.value,
  newUrl => {
    if (newUrl) {
      retryCount.value = 0;
      loadAudio(newUrl);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (retryTimeoutId) {
    clearTimeout(retryTimeoutId);
  }
});

const handleAudioError = () => {
  // Se o áudio visível der erro, tentar recarregar
  if (!isLoading.value && displayUrl.value) {
    retryCount.value = 0;
    loadAudio(timeStampURL.value);
  }
};

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
  // Initialize playback rate when audio is ready
  if (audioPlayer.value) {
    audioPlayer.value.playbackRate = playbackSpeed.value;
  }
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  if (!audioPlayer.value) return;
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  if (!audioPlayer.value) return;
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (!audioPlayer.value) return;
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  if (audioPlayer.value) {
    audioPlayer.value.playbackRate = 1;
  }
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  if (audioPlayer.value) {
    audioPlayer.value.playbackRate = playbackSpeed.value;
  }
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    v-if="displayUrl"
    ref="audioPlayer"
    controls
    class="hidden"
    :src="displayUrl"
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @error="handleAudioError"
  />
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 flex flex-col items-center border shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
    :class="[
      isCallMessage && callStyles
        ? [callStyles.borderColor, callStyles.bgColor]
        : 'bg-n-alpha-white border-n-container',
    ]"
  >
    <!-- Header de chamada (só aparece se for chamada) -->
    <div
      v-if="isCallMessage && callStyles"
      class="flex items-center gap-2 w-full px-1 pb-1 border-b"
      :class="callStyles.borderColor"
    >
      <div
        class="flex items-center justify-center w-6 h-6 rounded-full"
        :class="callStyles.iconBg"
      >
        <PhoneIncoming
          v-if="isIncomingCall"
          class="w-3.5 h-3.5"
          :class="callStyles.textColor"
        />
        <PhoneOutgoing
          v-else
          class="w-3.5 h-3.5"
          :class="callStyles.textColor"
        />
      </div>
      <span class="text-xs font-medium" :class="callStyles.textColor">
        {{ callStyles.label }}
      </span>
    </div>

    <!-- Player de áudio -->
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button
        class="p-0 border-0 size-8"
        :disabled="isLoading || hasError"
        @click="playOrPause"
      >
        <Icon
          v-if="isLoading"
          class="size-8 animate-spin"
          icon="i-lucide-loader-2"
        />
        <Icon
          v-else-if="hasError"
          class="size-8 text-n-slate-9"
          icon="i-lucide-circle-off"
        />
        <Icon
          v-else-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 rounded-lg appearance-none cursor-pointer accent-current"
          :class="
            isCallMessage && callStyles
              ? 'bg-n-slate-12/20'
              : 'bg-n-slate-12/40'
          "
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center hover:bg-alpha-3 rounded-2xl"
        :class="
          isCallMessage && callStyles ? callStyles.iconBg : 'bg-n-alpha-2'
        "
        @click="changePlaybackSpeed"
      >
        <span
          class="text-xs font-medium"
          :class="
            isCallMessage && callStyles
              ? callStyles.textColor
              : 'text-n-slate-11'
          "
        >
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="showTranscription"
      class="p-3 text-sm rounded-lg w-full break-words"
      :class="
        isCallMessage && callStyles
          ? [callStyles.iconBg, callStyles.textColor]
          : 'text-n-slate-12 bg-n-alpha-1'
      "
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>
